define('chatInboxListView',[
  'module',
  'app',
  'backbone',
  'underscore',
  'jquery',
  'settings',
  'tableView',
  'tableFieldView',
  'notificationCenter',
  'entities/chat_inbox'
], function (Module, App, Backbone, _, $, Settings, TableView, FieldView, NotificationCenter) {
  'use strict';

  var FILTER_STORAGE_KEY = 'chatInbox.listFilters';

  // Short "x min ago" label for the roster's activity timestamp.
  function relativeTime(date) {
    if (!date) { return ''; }
    var diff = Date.now() - date.getTime();
    var min = Math.round(diff / 60000);
    if (min < 1)  { return 'now'; }
    if (min < 60) { return min + 'm'; }
    var hr = Math.round(min / 60);
    if (hr < 24)  { return hr + 'h'; }
    return Math.round(hr / 24) + 'd';
  }

  // Manual status transitions an agent can apply straight from the roster.
  // The backend only exposes resolve/reopen as true status setters (claim and
  // release change assignment, not status; AI_HANDLING/ABANDONED are
  // system-driven), so an active chat can be resolved and a closed one
  // reopened back into the unclaimed queue.
  function statusTransitions(status) {
    if (status === 'SPAM') {
      // Auto-quarantined junk. "Not spam" reuses the reopen setter (the
      // backend accepts SPAM there too), pushing the chat back into the
      // unclaimed queue for a genuine visitor that was mis-flagged.
      return [{
        label: 'Not spam',
        request: 'chatInbox:reopen',
        confirm: 'Mark as not spam? It returns to the unclaimed queue.'
      }];
    }
    if (status === 'RESOLVED' || status === 'ABANDONED') {
      return [{
        label: 'Reopen',
        request: 'chatInbox:reopen',
        confirm: 'Re-open this chat? It returns to the unclaimed queue.'
      }];
    }
    return [{
      label: 'Resolve',
      request: 'chatInbox:resolve',
      confirm: 'Mark this chat as resolved?'
    }];
  }

  // ---------------------------------------------------------------------
  // One conversation, rendered as a single rich cell (mailbox-style) so the
  // roster reads like an email list in the narrow left pane: visitor + time on
  // top, status / locale / assignee beneath. Re-renders on any model change so
  // live WS updates (status, claim, presence) stay in sync. The status chip
  // doubles as a quick menu to resolve / reopen without opening the chat.
  // ---------------------------------------------------------------------
  var ConversationRowView = FieldView.extend({
    className: 'table-field-view ci-cell',

    template: _.template(
      '<div class="ci-row-top">' +
      '  <span class="presence-dot <%= online ? \'on\' : \'off\' %>" title="<%= online ? \'Customer connected\' : \'Customer offline\' %>"></span>' +
      '  <span class="ci-name" title="<%= name %>"><%= name %></span>' +
      '  <span class="ci-time"><%= timeLabel %></span>' +
      '</div>' +
      '<div class="ci-row-bottom">' +
      '  <span class="status-chip s-<%= status %> interactive" title="Change status">' +
      '    <%= statusLabel %><span class="chip-caret">&#9662;</span>' +
      '  </span>' +
      '  <% if (locale) { %><span class="ci-locale"><%= locale %></span><% } %>' +
      '  <span class="ci-assignee <%= assignee ? \'\' : \'unassigned\' %>"><%= assignee || \'Unclaimed\' %></span>' +
      '  <div class="ci-status-menu" style="display:none"></div>' +
      '</div>'
    ),

    events: {
      'click .status-chip': 'onStatusChipClick',
      'click .ci-status-opt': 'onStatusOptionClick'
    },

    serializeData: function () {
      var m = this.model;
      var cm = m.get('lastCustomerMessageAt');
      var am = m.get('lastAgentMessageAt');
      var most = (cm && am) ? (new Date(cm) > new Date(am) ? cm : am) : (cm || am || m.get('startedAt'));
      var status = m.get('status') || 'UNKNOWN';
      return {
        online: !!m.get('online'),
        name: _.escape(m.get('customerName') || m.get('senderName') || m.get('senderEmail') || 'Anonymous'),
        timeLabel: most ? relativeTime(new Date(most)) : '',
        status: status,
        statusLabel: status.replace(/_/g, ' ').toLowerCase(),
        locale: m.get('locale') ? (m.get('locale') + '').toUpperCase() : '',
        assignee: m.get('assignedUserpidName') ? _.escape(m.get('assignedUserpidName')) : ''
      };
    },

    // The whole cell is rendered from serializeData via the template; the base
    // FieldView.onRender would try to bind a single attribute, so skip it. A
    // re-render (live WS update) also discards any open status menu, so reset
    // the menu state and drop its outside-click handler.
    onRender: function () {
      this.closeStatusMenu();
    },

    // Open/close the quick status menu. stopPropagation keeps the row's own
    // click (which opens the conversation) from firing.
    onStatusChipClick: function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (this._menuOpen) { this.closeStatusMenu(); return; }
      this.openStatusMenu();
    },

    openStatusMenu: function () {
      var self = this;
      var status = this.model.get('status') || 'WAITING_HUMAN';
      this._statusOpts = statusTransitions(status);
      var html = _.map(this._statusOpts, function (o, i) {
        return '<button type="button" class="ci-status-opt" data-index="' + i + '">' +
          _.escape(o.label) + '</button>';
      }).join('');
      this.$('.ci-status-menu').html(html).show();
      this._menuOpen = true;

      // Close on any click outside the menu. Deferred so the click that
      // opened the menu doesn't immediately bubble to the document and shut
      // it again.
      this._docClose = function () { self.closeStatusMenu(); };
      _.defer(function () {
        if (self.isDestroyed) { return; }
        $(document).on('click', self._docClose);
      });
    },

    closeStatusMenu: function () {
      this._menuOpen = false;
      var $menu = this.$('.ci-status-menu');
      if ($menu.length) { $menu.hide().empty(); }
      if (this._docClose) {
        $(document).off('click', this._docClose);
        this._docClose = null;
      }
    },

    onStatusOptionClick: function (e) {
      e.preventDefault();
      e.stopPropagation();
      var opt = this._statusOpts && this._statusOpts[$(e.currentTarget).data('index')];
      this.closeStatusMenu();
      if (opt) { this.applyStatus(opt); }
    },

    applyStatus: function (opt) {
      if (opt.confirm && !window.confirm(opt.confirm)) { return; }
      var self = this;
      var secId = this.model.get('secId');
      this.$('.status-chip').addClass('busy');
      App.request(opt.request, secId).done(function () {
        if (self.isDestroyed) { return; }
        // Soft-refresh the roster so server-side filters re-apply (a resolved
        // chat drops out when "Hide resolved" is on) and the count stays in
        // sync — same path the WS lifecycle events already use.
        var tableView = self.options.tableView;
        if (tableView && tableView.refresh) { tableView.refresh(); }
      }).fail(function () {
        if (self.isDestroyed) { return; }
        self.$('.status-chip').removeClass('busy');
        window.alert('Could not change the status. Please try again.');
      });
    },

    onDestroy: function () {
      this.closeStatusMenu();
    }
  });

  function loadFilters() {
    try {
      var raw = window.localStorage ? window.localStorage.getItem(FILTER_STORAGE_KEY) : null;
      return raw ? JSON.parse(raw) : {};
    } catch (e) { return {}; }
  }

  function saveFilters(filters) {
    try {
      if (window.localStorage) {
        window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters || {}));
      }
    } catch (e) { /* ignore quota / privacy-mode errors */ }
  }

  Module.exports = TableView.extend({
    className: 'chat-inbox-list-view ' + TableView.prototype.className,
    pageMode: 'pagination',
    pageSize: 50,
    selectable: false,

    initialize: function () {
      this.filters = loadFilters();
      // Default to hiding resolved chats — the inbox is meant for active
      // conversations. Persisted so the user can override and have it stick.
      if (typeof this.filters.hideResolved === 'undefined') {
        this.filters.hideResolved = true;
      }
      if (TableView.prototype.initialize) {
        TableView.prototype.initialize.apply(this, arguments);
      }
    },

    onBeforeShow: function () {
      if (TableView.prototype.onBeforeShow) {
        TableView.prototype.onBeforeShow.apply(this, arguments);
      }
      this.renderFiltersBar();
      this.subscribeToNotifications();
    },

    /**
     * The chat inbox is meant to feel "live": new sessions, claims, and
     * messages from other agents should show up without a manual refresh.
     * We piggyback on the existing WS notification fabric — every chat
     * lifecycle event triggers a soft refresh of the rows. addListener()
     * auto-unsubscribes on view destroy.
     */
    subscribeToNotifications: function () {
      var nc = NotificationCenter.getInstance();
      // Kept on `this` so onDestroy can cancel a pending trailing call —
      // addListener's off() stops *new* events reaching us, but a debounce
      // already in flight when the view is destroyed would still fire.
      this.debouncedRefresh = _.debounce(_.bind(this.refresh, this), 200);
      nc.addListener(this, NotificationCenter.events.CHAT_SESSION_STARTED, this.debouncedRefresh);
      nc.addListener(this, NotificationCenter.events.CHAT_SESSION_CLAIMED, this.debouncedRefresh);
      nc.addListener(this, NotificationCenter.events.CHAT_SESSION_RELEASED, this.debouncedRefresh);
      nc.addListener(this, NotificationCenter.events.CHAT_SESSION_RESOLVED, this.debouncedRefresh);
      nc.addListener(this, NotificationCenter.events.CHAT_SESSION_DELETED, this.debouncedRefresh);
      nc.addListener(this, NotificationCenter.events.CHAT_MESSAGE_ADDED, this.debouncedRefresh);
    },

    /**
     * True when a fetch result must NOT be applied to the table. The body
     * view (a CollectionView) and its row views can be torn down while a
     * request is still in flight — the list view is destroyed on navigation,
     * and LayoutView.destroy() tears down the regions (body view + rows)
     * *before* it sets the list view's own isDestroyed flag. Feeding rows
     * into that half-dead CollectionView makes it render child views that
     * were already destroyed → ViewDestroyedError. Checking the body view's
     * own health (not just the list view's flag) closes that window.
     */
    isStale: function () {
      return this.isDestroyed ||
        !this.bodyView ||
        this.bodyView.isDestroyed ||
        !this.bodyView.isRendered;
    },

    /**
     * Soft-refresh the rows in place. Serialised on purpose: a busy inbox
     * fires WS events in bursts, and overlapping bodyView.fetch() calls
     * abort one another — each abort resolves the previous fetch empty and
     * briefly clears every row. Here at most one fetch runs at a time;
     * extra requests collapse into a single trailing refresh that fires
     * once the current one settles.
     */
    refresh: function () {
      if (this.isStale()) { return; }
      if (this._refreshing) {
        this._refreshPending = true;
        return;
      }
      this._refreshing = true;
      var self = this;
      this.bodyView.fetch().always(function () {
        self._refreshing = false;
        if (self.isStale()) { return; }
        if (self._refreshPending) {
          self._refreshPending = false;
          self.refresh();
        }
      });
    },

    onDestroy: function () {
      // Cancel anything that could call back into a destroyed view: a
      // pending debounce and the in-flight list request.
      if (this.debouncedRefresh && this.debouncedRefresh.cancel) {
        this.debouncedRefresh.cancel();
      }
      if (this.fetchRequest) {
        this.fetchRequest.abort();
        this.fetchRequest = null;
      }
      if (TableView.prototype.onDestroy) {
        TableView.prototype.onDestroy.apply(this, arguments);
      }
    },

    renderFiltersBar: function () {
      var self = this;
      if (this.$el.find('.chat-inbox-filters').length) { return; }

      var bar = $('<div class="chat-inbox-filters"></div>');
      var mySecId = (Settings.get('currentUser') && Settings.get('currentUser').secId) || null;

      var unassignedLabel = $('<label class="ci-unassigned"><input type="checkbox"/> Unclaimed only</label>');
      var mineLabel = $('<label class="ci-mine"><input type="checkbox"/> Mine</label>');
      var hideResolvedLabel = $('<label class="ci-hide-resolved"><input type="checkbox"/> Hide resolved</label>');
      // Spam review folder — normally hidden. When on, the roster shows ONLY
      // the auto-quarantined sessions (server status=SPAM) so an agent can
      // scan for a genuine visitor that was mis-flagged and click "Not spam".
      var spamLabel = $('<label class="ci-spam"><input type="checkbox"/> Spam</label>');

      var $unassigned = unassignedLabel.find('input');
      var $mine = mineLabel.find('input');
      var $hideResolved = hideResolvedLabel.find('input');
      var $spam = spamLabel.find('input');

      $unassigned.prop('checked', !!this.filters.unassigned);
      $mine.prop('checked', !!this.filters.mine);
      $hideResolved.prop('checked', !!this.filters.hideResolved);
      $spam.prop('checked', !!this.filters.spam);

      $unassigned.on('change', function () {
        self.filters.unassigned = $(this).is(':checked') ? true : null;
        if (self.filters.unassigned) {
          self.filters.mine = null;
          $mine.prop('checked', false);
          self.filters.spam = null;
          $spam.prop('checked', false);
        }
        saveFilters(self.filters);
        self.refresh();
      });
      $mine.on('change', function () {
        if (!mySecId) { $(this).prop('checked', false); return; }
        self.filters.mine = $(this).is(':checked') ? true : null;
        if (self.filters.mine) {
          self.filters.unassigned = null;
          $unassigned.prop('checked', false);
          self.filters.spam = null;
          $spam.prop('checked', false);
        }
        saveFilters(self.filters);
        self.refresh();
      });
      $hideResolved.on('change', function () {
        self.filters.hideResolved = $(this).is(':checked') ? true : null;
        saveFilters(self.filters);
        self.refresh();
      });
      $spam.on('change', function () {
        // Viewing the spam bucket is its own mode: it overrides the other
        // filters (they'd only ever narrow it to nothing) and buildParams
        // sends status=SPAM instead of them.
        self.filters.spam = $(this).is(':checked') ? true : null;
        if (self.filters.spam) {
          self.filters.unassigned = null;
          self.filters.mine = null;
          $unassigned.prop('checked', false);
          $mine.prop('checked', false);
        }
        saveFilters(self.filters);
        self.refresh();
      });

      bar.append(unassignedLabel).append(mineLabel).append(hideResolvedLabel).append(spamLabel);
      this.$el.prepend(bar);
    },

    // A single rich cell per row — the roster reads like a mailbox list in the
    // split layout's left pane. modelEvent 'change' keeps the cell live as WS
    // events mutate the session (status / claim / presence / last activity).
    setFields: function () {
      return [
        { name: 'conversation', className: 'ci-cell', modelEvent: 'change', view: ConversationRowView }
      ];
    },

    // Header would just clutter a single-column roster; the table-view base
    // already hides it when `header` is false.
    header: false,

    onShow: function () {
      if (TableView.prototype.onShow) {
        TableView.prototype.onShow.apply(this, arguments);
      }
      // Re-apply the active highlight whenever the roster changes underneath us
      // (live refresh adds/removes rows); the row class lives on .table-row-view
      // so it survives an individual cell re-render but not a row teardown.
      this.listenTo(this.bodyView.collection, 'update reset', this.applySelection);
      this.applySelection();
    },

    // The split parent calls this when a conversation is opened on the right so
    // the matching row reads as selected; null clears it.
    setSelected: function (secId) {
      this.selectedSecId = secId || null;
      this.applySelection();
    },

    applySelection: function () {
      if (!this.bodyView || !this.bodyView.children) { return; }
      var sel = this.selectedSecId;
      this.bodyView.children.each(function (rowView) {
        var match = !!sel && rowView.model && rowView.model.get('secId') === sel;
        rowView.$el.toggleClass('ci-selected', match);
      });
    },

    buildParams: function (first, pageSize) {
      var params = { first: first, pageSize: pageSize };
      // Spam mode is exclusive: status=SPAM is the only server-side reveal
      // for quarantined sessions (they're excluded from every other view),
      // so it stands alone and the other filters don't apply.
      if (this.filters.spam) {
        params.status = 'SPAM';
        return params;
      }
      if (this.filters.unassigned)  { params.unassigned = true; }
      if (this.filters.mine)        { params.mine = true; }
      // "Hide resolved" is now applied server-side so paging and the row
      // count stay consistent (a client-side filter would shrink pages
      // below pageSize and desync the total).
      if (this.filters.hideResolved) { params.hideResolved = true; }
      return params;
    },

    fetch: function (first, pageSize) {
      var self = this;
      var defer = $.Deferred();
      var params = this.buildParams(first, pageSize);

      if (this.fetchRequest) { this.fetchRequest.abort(); }

      this.fetchRequest = $.ajax({
        type: 'GET',
        url: App.request('chatInbox:list-url', params),
        success: function (items) {
          // The body view may have been destroyed while this request was
          // in flight (navigation, or a raced burst of WS refreshes).
          // Resolve with [] so the TableView base's add/remove logic runs
          // on an empty set instead of rendering rows into a dead
          // CollectionView — which throws ViewDestroyedError.
          if (self.isStale()) { defer.resolve([]); return; }
          var models = _.map(items || [], function (json) {
            return App.request('chatInbox:model', json);
          });
          defer.resolve(models);
        },
        // On abort/failure resolve with []: it's safe regardless of view
        // state and lets the body view's deferred settle so a serialised
        // refresh can release its lock.
        error: function () {
          defer.resolve([]);
        }
      });

      return defer.promise();
    },

    // Real total so the pagination footer can offer more than one page.
    // Uses the same filters as the list (minus paging) so the count matches
    // the rows being paged through.
    fetchCount: function () {
      var defer = $.Deferred();
      var params = this.buildParams(0, this.pageSize);
      delete params.first;
      delete params.pageSize;
      if (this.fetchCountRequest) { this.fetchCountRequest.abort(); }
      this.fetchCountRequest = $.ajax({
        type: 'GET',
        url: App.request('chatInbox:count-url', params),
        success: function (resp) { defer.resolve(resp); },
        error: function () { defer.resolve(0); }
      });
      return defer.promise();
    },

    onRowClick: function (rowView) {
      var secId = rowView.model.get('secId');
      App.navigate('chat-inbox/' + secId, { trigger: true });
    }
  });
});

